import React from 'react';
import { motion } from 'framer-motion';
import { Calendar, MapPin, ExternalLink } from 'lucide-react';

interface WorkExperience {
  id: number;
  company: string;
  position: string;
  location: string;
  duration: string;
  description: string;
  achievements: string[];
  technologies?: string[];
}

const experiences: WorkExperience[] = [
  {
    id: 1,
    company: "Splunk, a Cisco company",
    position: "Senior Product Manager",
    location: "California, USA",
    duration: "September 2021 - Present",
    description: "Responsible for the Data Collection and Agent Management experience for Splunk Observability, supporting 300+ integrations to enable 2000+ customers ingesting up to 1.1 billion datapoints/minute",
    achievements: [
      "Named as co-inventor for two patents, one issued for 'Performing Iterative Entity Discovery and Instrumentation' and filed for Creating and Maintaining an Entity Instrumentation Inventory, successfully launching Getting Data In Inventory feature",
      "Tripled OpenTelemetry customer adoption for Splunk Observability by ideating strategy, proposing improvements and guidance. Played a key role in Splunk being named as a Leader in the 2023, 2024 Gartner Magic Quadrant for APM",
      "Reduced Time To Value and steps to get data in with OpenTelemetry by 67% by launching a competitive differentiator - Automatic Discovery and Configuration across both, hosts and Kubernetes infrastructure",
      "Improved logging performance by 10x by launching unified agent deployment experience with the OpenTelemetry Collector. Articulated detailed product requirements and collaborated with multiple engineering teams across different organizations",
      "Led requirements gathering and currently driving the execution strategy for making the Splunk Observability Cloud platform compliant with federal security standards; contributed to achieving 'FedRAMP In Process' status",
      "Collaborated with GCP, AWS and RedHat for joint product releases and partnership engagements such as webinars, presenting capabilities at lightning sessions at KubeCon North America, 2022 and 2023",
      "Mentored two Product Managers, fostering their career growth, enabling them to successfully deliver high-priority features closely aligned with key adoption relevant OKRs and an increase in customer retention"
    ],
    technologies: ["OpenTelemetry", "Kubernetes", "AWS", "GCP", "Observability", "Product Strategy", "FedRAMP"]
  },
  {
    id: 2,
    company: "Shutterfly, Inc",
    position: "Senior Software Engineer I",
    location: "California, USA",
    duration: "February 2019 - September 2021",
    description: "Led development initiatives focused on cloud migration, cost optimization, and feature flag implementation for cloud backend services",
    achievements: [
      "Awarded the YouRock recognition for demonstrating the 'We act as owners' value for milestones on the 2020 project roadmap",
      "Reduced image storage costs in AWS by 40% and on-prem footprint by 75%, accomplishing 100 petabytes of data migration",
      "Created detailed user stories, epics on JIRA based on priority and stakeholder commitment and ran two-week agile sprints",
      "Contributed to key open source project Feature Flags For Java and set up dynamic configuration for cloud backend service"
    ],
    technologies: ["Java", "AWS", "JIRA", "Agile", "Feature Flags", "Cloud Migration"]
  },
  {
    id: 3,
    company: "Chewy.com",
    position: "Software Engineer Co-op",
    location: "Boston, USA",
    duration: "June 2017 - December 2017",
    description: "Developed customer search functionality and product discovery features to improve user experience and service efficiency",
    achievements: [
      "Improved customer service response time by 30% by designing and developing customer search core with Solr and Lucene",
      "Increased product discovery by 2x by analyzing top 1M queries with SQL to prepare data for autocomplete service"
    ],
    technologies: ["Solr", "Lucene", "SQL", "Search", "Autocomplete"]
  }
];

const Experience: React.FC = () => {
  return (
    <section id="experience" className="section-padding bg-gray-50">
      <div className="container-max">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Work Experience
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Over 6 years of experience combining software engineering expertise with product management, 
            delivering impactful solutions at scale.
          </p>
        </motion.div>

        <div className="space-y-12">
          {experiences.map((experience, index) => (
            <motion.div
              key={experience.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow duration-300"
            >
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between mb-6">
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    {experience.position}
                  </h3>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6 text-gray-600 mb-4">
                    <div className="flex items-center gap-2">
                      <ExternalLink className="w-4 h-4" />
                      <span className="font-semibold text-primary-600">{experience.company}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      <span>{experience.location}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      <span>{experience.duration}</span>
                    </div>
                  </div>
                  <p className="text-gray-700 leading-relaxed mb-6">
                    {experience.description}
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <h4 className="font-semibold text-gray-900">Key Achievements:</h4>
                <ul className="space-y-3">
                  {experience.achievements.map((achievement, achievementIndex) => (
                    <motion.li
                      key={achievementIndex}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.4, delay: achievementIndex * 0.05 }}
                      viewport={{ once: true }}
                      className="flex items-start gap-3"
                    >
                      <div className="w-2 h-2 bg-primary-600 rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-gray-700 leading-relaxed">{achievement}</span>
                    </motion.li>
                  ))}
                </ul>
              </div>

              {experience.technologies && (
                <div className="mt-6">
                  <h4 className="font-semibold text-gray-900 mb-3">Technologies & Skills:</h4>
                  <div className="flex flex-wrap gap-2">
                    {experience.technologies.map((tech, techIndex) => (
                      <motion.span
                        key={techIndex}
                        initial={{ opacity: 0, scale: 0.8 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.3, delay: techIndex * 0.05 }}
                        viewport={{ once: true }}
                        className="px-3 py-1 bg-primary-100 text-primary-800 text-sm rounded-full"
                      >
                        {tech}
                      </motion.span>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Experience;
